'use strict';

module.exports = /* @ngInject */ function (apiConfig, sseService, httpService) {

    function getBaseShipping() {
        return `${apiConfig.$get().apiUrl}/customer/address/shipping`
    }

    /**
     * Get all shipping addresses activated for the current user
     * @typedef {
     *     {
	 *        shippingAddressCode: string,
     *        companyAddressWithContact: {
	 *            id: number,
     *            companyAddress: {
     *                companyName: {
     *                    line1: string,
     *                    line2: string
     *                },
     *                address: {
     *                    street: string,
     *                    zip: string,
     *                    city: string,
     *                    state: string,
     *                    country: {
     *                        iso3166: string,
     *                        known: boolean,
     *                        standardized: boolean,
     *                        value: string
     *                    }
     *                },
     *                emailAddress: {
     *                    value: string
     *                }
     *            },
     *            personName: {
     *                firstname: string,
     *                lastname: string
     *            }
     *        }
     *     }
     * } ShippingAddressResponse
     *
     * @return {
     *     Promise<ShippingAddressResponse[]>
     * }
     */
    function getAllShipping() {
        const url = `${getBaseShipping()}/all`;
        return httpService.get(url, {
            isArray: true
        });
    }

    /**
     * Get my default shipping address
     @return {
      *     Promise<ShippingAddressResponse>
      * }
     */
    function getMyDefaultShipping() {
        const url = `${getBaseShipping()}/default`;
        return httpService.get(url);
    }

    /**
     * Site-aware delivery-address candidate list for the document header (EPROC-10910).
     * Loads the union of the positions' site addresses and the user's whitelist from the shared,
     * document-type-agnostic candidate endpoint (identical data basis as the neucode) and maps it to
     * the {@link ShippingAddressResponse} shape the address-selection directive renders.
     *
     * @param documentContext {'BASKET'|'RFQ'} which document the candidates are for
     * @param rfqBasketId {string=} the RFQ request basket id (VS_INDEX); required when context is 'RFQ'
     * @param basketId {string=} the legacy basket id (VS_INDEX = session id); required when context is
     *        'BASKET'. Both contexts hit their own dedicated legacy endpoint, which reads the positions'
     *        site codes from VS_RFQ_BASKETITEMS / VS_BASKETITEMS (the legacy shop keeps the picked site
     *        there, never mirrored to basket_item_v2), so the site restriction applies here too
     * @return {Promise<{addresses: ShippingAddressResponse[], autoSelectId: (number|null)}>}
     */
    function getShippingCandidates(documentContext, rfqBasketId, basketId) {
        const base = apiConfig.$get().apiUrl;
        const url = documentContext === 'RFQ'
            ? `${base}/rfq-basket/${rfqBasketId}/delivery-address-candidates`
            : `${base}/legacy-basket/${basketId}/delivery-address-candidates`;
        return httpService.get(url).then((view) => ({
            addresses: (view && view.candidates ? view.candidates : []).map(candidateToShippingAddress),
            autoSelectId: view && view.autoSelectAddressId != null ? view.autoSelectAddressId : null,
        }));
    }

    /**
     * Maps a candidate from the delivery-address candidate endpoint to the shared
     * {@link ShippingAddressResponse} shape, so the existing directive/template can present the union
     * and persist the selection via the address id.
     */
    function candidateToShippingAddress(candidate) {
        return {
            shippingAddressCode: candidate.deliveryAddressCode,
            sources: candidate.sources,
            companyAddressWithContact: {
                id: candidate.id,
                companyAddress: {
                    companyName: {
                        line1: candidate.companyName,
                        line2: candidate.companyName2,
                    },
                    address: {
                        street: candidate.street,
                        zip: candidate.zip,
                        city: candidate.city,
                        state: candidate.state,
                        country: {
                            value: candidate.country,
                            iso3166: 'ALPHA_2',
                        },
                    },
                    emailAddress: {
                        value: '',
                    },
                },
                personName: {
                    firstname: candidate.firstName,
                    lastname: candidate.surname,
                },
            },
        };
    }

    return {
        getAllShipping,
        getMyDefaultShipping,
        getShippingCandidates
    }
}
